import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { logger } from '../utils/logger';
import api from '../services/api';
import AdminLayout from '../components/admin/AdminLayout';
import CurriculumDropdownWithCreate from '../components/admin/CurriculumDropdownWithCreate';
import GradeDropdownWithCreate from '../components/admin/GradeDropdownWithCreate';
import SubjectDropdownWithCreate from '../components/admin/SubjectDropdownWithCreate';

console.log('Rendering AdminMusicUpload');

const initialState = {
  excelFile: null,
  audioFiles: [],
  parsedRows: [],
  matchedFiles: {},
  errors: [],
  uploading: false,
  uploadProgress: 0,
  uploadResults: [],
  uploadSuccess: false,
};

const AdminMusicUpload = () => {
  const [state, setState] = useState(initialState);
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedCurriculum, setSelectedCurriculum] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [coverImages, setCoverImages] = useState([]);
  const [artist, setArtist] = useState('EduVibz');
  const [genre, setGenre] = useState('Teen Pop');

  // Handle Excel file selection and parse
  const handleExcelChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);
      // DEBUG: Log spreadsheet columns and first row
      if (rows.length > 0) {
        console.log('[DEBUG] Spreadsheet columns:', Object.keys(rows[0]));
        console.log('[DEBUG] First row:', rows[0]);
      } else {
        console.log('[DEBUG] No rows parsed from spreadsheet.');
      }
      // Map spreadsheet columns to backend fields
      const normalizedRows = rows.map(row => ({
        ...row,
        chapterName: row["Chapter"],
        subjectId: selectedSubject,
        gradeId: selectedGrade,
        curriculumId: selectedCurriculum,
        // Mappings for backend Music model
        title: row["Topic"],
        chapterNumber: row["Chapter"],
        artist: artist,
        album: row["Chapter"],
        genre: genre
        // duration removed; backend will set
      }));
      setState((s) => ({ ...s, excelFile: file, parsedRows: normalizedRows, errors: [] }));
      logger.info('Excel parsed', { rowCount: normalizedRows.length });
    } catch (error) {
      logger.error('Failed to parse Excel', { error });
      setState((s) => ({ ...s, errors: ['Invalid Excel file.'] }));
    }
  };

  // Handle multiple audio files selection
  const handleAudioChange = (e) => {
    const files = Array.from(e.target.files);
    setState((s) => ({ ...s, audioFiles: files, errors: [] }));
  };

  // Handle bulk cover images selection
  const handleCoverImagesChange = (e) => {
    setCoverImages(Array.from(e.target.files));
  };

  // Validate audio files against parsed spreadsheet rows
  const validateFiles = () => {
    const { parsedRows, audioFiles } = state;
    if (!parsedRows.length || !audioFiles.length) {
      return ['Please select both a spreadsheet and audio files.'];
    }
    // Auto-detect the filename column (case-insensitive, ignore spaces/underscores)
    const firstRow = parsedRows[0] || {};
    const filenameKey = Object.keys(firstRow).find(
      k => k && k.replace(/[^a-z]/gi, '').toLowerCase().includes('filename')
    );
    if (!filenameKey) {
      console.error('[validateFiles] Could not find a filename column in spreadsheet. Available keys:', Object.keys(firstRow));
      return ['Could not find a filename column in your spreadsheet. Please ensure there is a column named "Filename".'];
    }
    // Normalization helper
    const normalize = (str) =>
      (str || '')
        .toLowerCase()
        .replace(/[_\s]+/g, '') // remove underscores and spaces
        .replace(/[^a-z0-9.]/g, '') // remove all but alphanumeric, dot, and numbers
        .trim();
    // Build lookup maps
    const spreadsheetNamesRaw = parsedRows.map((row) => row[filenameKey]);
    const spreadsheetNamesNorm = spreadsheetNamesRaw.map(normalize);
    const audioNamesRaw = audioFiles.map((f) => f.name);
    const audioNamesNorm = audioNamesRaw.map(normalize);
    // Debug mapping
    console.log('[validateFiles] spreadsheetNamesRaw:', spreadsheetNamesRaw);
    console.log('[validateFiles] spreadsheetNamesNorm:', spreadsheetNamesNorm);
    console.log('[validateFiles] audioNamesRaw:', audioNamesRaw);
    console.log('[validateFiles] audioNamesNorm:', audioNamesNorm);
    // Map normalized to original for reporting
    const audioNormToRaw = Object.fromEntries(audioNamesNorm.map((n, i) => [n, audioNamesRaw[i]]));
    const spreadsheetNormToRaw = Object.fromEntries(spreadsheetNamesNorm.map((n, i) => [n, spreadsheetNamesRaw[i]]));
    // Find missing and extra using normalized names
    const missing = spreadsheetNamesNorm.filter((n) => !audioNamesNorm.includes(n)).map((n) => spreadsheetNormToRaw[n]);
    const extra = audioNamesNorm.filter((n) => !spreadsheetNamesNorm.includes(n)).map((n) => audioNormToRaw[n]);
    // Show row indices for unmatched spreadsheet rows
    const unmatchedSpreadsheetRows = parsedRows
      .map((row, idx) => {
        const norm = normalize(row[filenameKey]);
        return !audioNamesNorm.includes(norm) ? { idx: idx + 1, filename: row[filenameKey] } : null;
      })
      .filter(Boolean);
    if (unmatchedSpreadsheetRows.length) {
      console.log('[validateFiles] Unmatched spreadsheet rows:', unmatchedSpreadsheetRows);
    }
    const errors = [];
    if (missing.length) errors.push(`Missing audio files: ${missing.join(', ')}`);
    if (extra.length) errors.push(`Unmatched audio files: ${extra.join(', ')}`);
    if (unmatchedSpreadsheetRows.length) errors.push(`Unmatched spreadsheet rows: ${unmatchedSpreadsheetRows.map(r => `[Row ${r.idx}: ${r.filename}]`).join(', ')}`);
    return errors;
  };

  // Validate cover images against parsed spreadsheet rows
  const validateCoverImages = () => {
    const { parsedRows } = state;
    if (!parsedRows.length || !coverImages.length) {
      return ['Please select both a spreadsheet and cover images.'];
    }
    // Find cover image column (case-insensitive)
    const firstRow = parsedRows[0] || {};
    const coverKey = Object.keys(firstRow).find(
      k => k && k.replace(/[^a-z]/gi, '').toLowerCase().includes('coverimage')
    );
    if (!coverKey) return ['Could not find a Cover Image column in your spreadsheet.'];
    const normalize = (str) => (str || '').toLowerCase().replace(/[_\s]+/g, '').replace(/[^a-z0-9.]/g, '').trim();
    const spreadsheetCoverNamesRaw = parsedRows.map((row) => row[coverKey] || '');
    const spreadsheetCoverNamesNorm = spreadsheetCoverNamesRaw.map(normalize);
    const coverImageNamesRaw = coverImages.map((f) => f.name);
    const coverImageNamesNorm = coverImageNamesRaw.map(normalize);
    // Map normalized to original for reporting
    const coverNormToRaw = Object.fromEntries(coverImageNamesNorm.map((n, i) => [n, coverImageNamesRaw[i]]));
    const missing = spreadsheetCoverNamesNorm.filter(
      n => n && !coverImageNamesNorm.includes(n)
    ).map(n => spreadsheetCoverNamesRaw[spreadsheetCoverNamesNorm.indexOf(n)]);
    const errors = [];
    if (missing.length) errors.push(`Missing cover images: ${missing.join(', ')}`);
    return errors;
  };

  // Upload handler
  const handleUpload = async () => {
    console.log('[AdminMusicUpload] handleUpload triggered', state, coverImages);
    logger.info('[AdminMusicUpload] handleUpload triggered', { state, coverImages });
    const errors = [...validateFiles(), ...validateCoverImages()];
    if (errors.length) {
      setState((s) => ({ ...s, errors }));
      console.log('[AdminMusicUpload] Validation errors', errors);
      logger.error('[AdminMusicUpload] Validation errors', { errors });
      return;
    }
    // Additional validation: ensure every row has a non-empty chapterName
    const missingChapters = state.parsedRows.filter(r => !r.chapterName || !r.chapterName.trim());
    if (missingChapters.length) {
      const chapterErrorMsg = `Spreadsheet error: ${missingChapters.length} row(s) missing a Chapter name. Please ensure every row has a value in the 'Chapter' column.`;
      setState(s => ({ ...s, errors: [chapterErrorMsg] }));
      logger.error('[AdminMusicUpload] Missing chapterName in spreadsheet rows', { missingChapters });
      return;
    }
    setState((s) => ({ ...s, uploading: true, uploadProgress: 0, errors: [], uploadResults: [], uploadSuccess: false }));
    try {
      const formData = new FormData();
      formData.append('spreadsheet', state.excelFile);
      formData.append('rows', JSON.stringify(state.parsedRows));
      state.audioFiles.forEach((file) => formData.append('audio', file));
      coverImages.forEach(file => formData.append('coverImage', file));
      console.log('[AdminMusicUpload] Submitting upload request', formData);
      logger.info('[AdminMusicUpload] Submitting upload request', { formDataKeys: Array.from(formData.keys()) });
      // Debug: log all FormData keys and types
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`[FormData] key=${key}, filename=${value.name}, type=${value.type}`);
        } else {
          console.log(`[FormData] key=${key}, value=${value}`);
        }
      }
      const res = await api.post('/admin/music', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          const percent = evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0;
          setState((s) => ({ ...s, uploadProgress: percent }));
        },
      });
      const results = res.data.results || [];
      const allSuccess = results.length > 0 && results.every(r => r.success);
      setState((s) => ({
        ...s,
        uploading: false,
        uploadResults: results,
        uploadProgress: 100,
        uploadSuccess: allSuccess,
        errors: allSuccess ? [] : results.filter(r => r.error).map(r => r.error)
      }));
      logger.info('Upload successful', { resultCount: results.length, allSuccess });
      // Reset form if all succeeded
      if (allSuccess) {
        setTimeout(() => setState(initialState), 2000);
      }
    } catch (error) {
      logger.error('Upload failed', { error });
      setState((s) => ({ ...s, uploading: false, errors: ['Upload failed. Please try again.'], uploadResults: [], uploadSuccess: false }));
    }
  };

  // UI
  const isUploadReady =
    selectedCurriculum &&
    selectedGrade &&
    selectedSubject &&
    state.excelFile &&
    state.audioFiles.length > 0 &&
    coverImages.length > 0 &&
    validateFiles().length === 0 &&
    validateCoverImages().length === 0 &&
    !state.uploading;

  const UploadFeedback = () => {
    if (state.uploading) {
      return <div className="text-eduvibe-blue font-semibold my-4">Uploading... Please wait.</div>;
    }
    if (state.uploadSuccess) {
      return <div className="text-eduvibe-green font-semibold my-4">Upload successful! {state.uploadResults.length} items processed.</div>;
    }
    if (state.errors && state.errors.length) {
      return <div className="text-eduvibe-red font-semibold my-4">{state.errors.join(' ')}</div>;
    }
    return null;
  };

  return (
    <AdminLayout>
      <div className="max-w-2xl mx-auto p-6 bg-[#1a2236] rounded shadow-lg text-white">
        <h2 className="text-2xl font-bold mb-4">Admin Music Upload</h2>
        <div className="mb-6">
          <CurriculumDropdownWithCreate
            value={selectedCurriculum}
            onChange={val => {
              setSelectedCurriculum(val);
              setSelectedGrade('');
              setSelectedSubject('');
            }}
          />
          <GradeDropdownWithCreate
            curriculumId={selectedCurriculum}
            value={selectedGrade}
            onChange={val => {
              setSelectedGrade(val);
              setSelectedSubject('');
            }}
          />
          <SubjectDropdownWithCreate
            curriculumId={selectedCurriculum}
            gradeId={selectedGrade}
            value={selectedSubject}
            onChange={setSelectedSubject}
          />
        </div>
        <div className="mb-6">
          <button
            className={`flex items-center gap-2 px-3 py-2 rounded font-semibold transition-colors
              ${showInstructions
                ? 'bg-eduvibe-green text-eduvibe-dark hover:bg-eduvibe-green-dark'
                : 'bg-eduvibe-blue text-white hover:bg-eduvibe-green hover:text-eduvibe-dark'}`}
            onClick={() => setShowInstructions(v => !v)}
            aria-expanded={showInstructions}
            aria-controls="music-upload-instructions"
            type="button"
          >
            <span>{showInstructions ? 'Hide' : 'Show'} Upload Instructions</span>
            <svg className={`w-4 h-4 transition-transform ${showInstructions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
          </button>
          {showInstructions && (
            <div id="music-upload-instructions" className="mt-4 p-4 bg-[#22304a] rounded border border-eduvibe-blue text-eduvibe-green text-sm animate-fade-in">
              <h3 className="font-semibold text-lg mb-2 text-eduvibe-green">Instructions</h3>
              <ul className="list-disc ml-6 text-eduvibe-green space-y-2">
                <li><b>Prepare an Excel spreadsheet (.xlsx)</b> with the following columns:
                  <ul className="ml-4 list-[circle] space-y-1">
                    <li><b>Filename</b> <span className="text-gray-300">(required, must match audio file exactly, e.g. <code>my_song_1.mp3</code>)</span></li>
                    <li><b>Topic</b> <span className="text-gray-300">(used as song title)</span></li>
                    <li><b>Chapter Number</b></li>
                    <li><b>Chapter</b> <span className="text-gray-300">(used as album name)</span></li>
                    <li><b>Lyrics</b> <span className="text-gray-300">(optional)</span></li>
                    <li><b>Cover Image</b> <span className="text-gray-300">(optional, specify exact filename of image, e.g. <code>chapter1.jpg</code>)</span></li>
                  </ul>
                </li>
                <li><b>Audio files</b> must be in MP3 or WAV format. Filenames must match the <b>Filename</b> column in the spreadsheet exactly (including extension).</li>
                <li>Spaces in filenames are discouraged; use underscores for best results.</li>
                <li>All songs must have a matching row in the spreadsheet, and all spreadsheet rows must have a matching audio file.</li>
                <li><b>Artist</b> and <b>Genre</b> will be specified below and applied to all songs in this upload.</li>
                <li>After validation, files are uploaded to <b>Cloudflare R2</b> and metadata is saved to the database. Existing songs are updated if a duplicate is found.</li>
                <li>Duration will be extracted <b>server-side</b> after upload.</li>
                <li>Example spreadsheet:
                  <div className="overflow-x-auto mt-2">
                    <table className="min-w-[400px] text-xs border bg-[#1a2236]">
                      <thead>
                        <tr>
                          <th className="border px-2 py-1">Filename</th>
                          <th className="border px-2 py-1">Topic</th>
                          <th className="border px-2 py-1">Chapter Number</th>
                          <th className="border px-2 py-1">Chapter</th>
                          <th className="border px-2 py-1">Lyrics</th>
                          <th className="border px-2 py-1">Cover Image</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border px-2 py-1">my_song_1.mp3</td>
                          <td className="border px-2 py-1">Photosynthesis Pop</td>
                          <td className="border px-2 py-1">2</td>
                          <td className="border px-2 py-1">Life Sciences</td>
                          <td className="border px-2 py-1">Photosynthesis, photosynthesis, makes the world green...</td>
                          <td className="border px-2 py-1">chapter1.jpg</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </li>
              </ul>
            </div>
          )}
        </div>
        <div className="mb-4">
          <label className="block font-semibold mb-1">Upload Excel Spreadsheet</label>
          <input type="file" accept=".xlsx,.xls" onChange={handleExcelChange} />
        </div>
        <div className="mb-4">
          <label className="block font-semibold mb-1">Upload Audio Files</label>
          <input type="file" accept="audio/*" multiple onChange={handleAudioChange} />
        </div>
        <div className="mb-4">
          <label htmlFor="coverImages" className="block font-bold mt-4">Cover Images (optional, multiple):</label>
          <input type="file" id="coverImages" multiple accept=".jpg,.jpeg,.png" onChange={handleCoverImagesChange} />
        </div>
        {state.errors.length > 0 && (
          <div className="mb-4 text-red-400">
            <ul>{state.errors.map((err, i) => <li key={i}>{err}</li>)}</ul>
          </div>
        )}
        <div className="mb-4 flex gap-4">
          <div>
            <label className="block font-semibold mb-1">Artist</label>
            <input
              type="text"
              value={artist}
              onChange={e => setArtist(e.target.value)}
              className="border px-2 py-1 rounded w-48"
              placeholder="Artist name"
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Genre</label>
            <input
              type="text"
              value={genre}
              onChange={e => setGenre(e.target.value)}
              className="border px-2 py-1 rounded w-48"
              placeholder="Genre"
            />
          </div>
        </div>
        <UploadFeedback />
        {state.uploading && (
          <div className="w-full bg-gray-700 rounded h-2 mb-4">
            <div
              className="bg-eduvibe-blue h-2 rounded transition-all"
              style={{ width: `${state.uploadProgress}%` }}
            ></div>
          </div>
        )}
        <button
          className={`w-full py-2 px-4 rounded font-bold text-lg transition-colors
            ${isUploadReady ? 'bg-eduvibe-green text-eduvibe-dark hover:bg-eduvibe-green-dark' : 'bg-gray-500 text-gray-300 cursor-not-allowed'}`}
          onClick={handleUpload}
          disabled={!isUploadReady}
          type="button"
        >
          {state.uploading ? 'Uploading...' : 'Upload Music'}
        </button>
        {/* Migration Button */}
        <button
          className="btn btn-warning mt-3"
          disabled={state.uploading}
          onClick={async () => {
            if (!window.confirm('Migrate test DB → production?')) return;
            try {
              await api.post('/admin/migrate-test-prod');
              alert('Migration completed');
            } catch (err) {
              alert('Migration failed: ' + (err.response?.data?.message || err.message));
            }
          }}
        >
          Migrate Test → Prod
        </button>
        {!state.uploading && state.uploadSuccess && (
          <div className="mt-2 text-green-500 font-semibold">All files uploaded successfully!</div>
        )}
        {!state.uploading && !state.uploadSuccess && state.uploadResults && state.uploadResults.length > 0 && (
          <div className="mt-2 text-yellow-400">
            Upload complete! {state.uploadResults.filter(r => r.success).length} succeeded,{' '}
            {state.uploadResults.filter(r => r.error).length} failed.
            <ul className="list-disc ml-6">
              {state.uploadResults.map((r, i) =>
                r.success ? (
                  <li key={i}>Row {r.row + 1}: Success</li>
                ) : (
                  <li key={i} className="text-red-600">Row {r.row + 1}: {r.error}</li>
                )
              )}
            </ul>
          </div>
        )}
        {!state.uploading && state.errors && state.errors.length > 0 && (
          <div className="mt-2 text-red-600">
            {state.errors.map((err, i) => <div key={i}>{err}</div>)}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMusicUpload;
